import {t} from "@lingui/core/macro";

import {Errors, KAError} from "@khan/logging";

import type {
    CourseChallenge,
    CourseLevelTopicPath,
    LearnableContentCode,
    LearnableContentKind,
    LessonChild,
    LessonLevelTopicPath,
    MasteryChallenge,
    QuizMetadata,
    TopicPath,
    UnitTestMetadata,
} from "../../types/types.ts";
import * as strings from "../strings.tsx";

/**
 * A helper function that creates a full URL for learnable content at the
 * given content path.
 */
export const constructURL = (path: TopicPath): string | null => {
    if (path.pathKind === "course-level-learnable-content") {
        return constructCourseLevelURL(path);
    }

    if (path.pathKind === "lesson-level-learnable-content") {
        return constructLessonLevelURL(path);
    }

    throw new KAError("Unknown topic path kind", Errors.Internal, {
        sentryData: {
            contexts: {extras: {path: JSON.stringify(path)}},
        },
    });
};

/**
 * A helper function that creates a full URL for course challenges at the
 * given content path, e.g. '/domain/course/test/course-challenge-slug'.
 */
const constructCourseLevelURL = (path: CourseLevelTopicPath): string => {
    const {domainSlug, courseSlug, learnableContentSlug} = path;
    const items = [domainSlug, courseSlug, "test", learnableContentSlug];
    items.forEach((item) => {
        if (!item) {
            throw new KAError(
                "Cannot construct URL for invalid path",
                Errors.Internal,
                {
                    sentryData: {
                        contexts: {extras: {path: JSON.stringify(path)}},
                    },
                },
            );
        }
    });
    return "/" + items.join("/");
};

/**
 * A helper function that creates a full DCUL URL for a given path, e.g.
 * '/domain/course/unit/lesson/e/exercise'
 */
const constructLessonLevelURL = (path: LessonLevelTopicPath): string | null => {
    const {
        domainSlug,
        courseSlug,
        unitSlug,
        lessonSlug,
        learnableContentKind,
        learnableContentSlug,
    } = path;
    const items = [
        domainSlug,
        courseSlug,
        unitSlug,
        lessonSlug,
        ContentCodeFromKind[learnableContentKind],
        learnableContentSlug,
    ];

    // Note (Salman, 2/24) - Not all content kinds are guaranteed to have
    // associated codes, such as AIGuideActivities (at the time of this
    // comment). Callers should handle the null case gracefully
    if (
        ValidLearnableContentKinds.includes(learnableContentKind) &&
        !ContentCodeFromKind[learnableContentKind]
    ) {
        return null;
    }

    items.forEach((item) => {
        if (!item) {
            throw new KAError(
                "Cannot construct URL for invalid path",
                Errors.Internal,
                {
                    sentryData: {
                        contexts: {extras: {path: JSON.stringify(path)}},
                    },
                },
            );
        }
    });
    return "/" + items.join("/");
};

/**
 * Helper function that converts a string to a LearnableContentKind.
 */
export const toContentKind = (kind: string): LearnableContentKind => {
    switch (kind) {
        case "Article":
        case "Challenge":
        case "Exercise":
        case "Interactive":
        case "Project":
        case "Talkthrough":
        case "Video":
            return kind;
        // Quizzes can be represented as TopicQuiz or Quiz
        case "TopicQuiz":
        case "Quiz":
            return "Quiz";
        // Unit Tests can be represented as TopicUnitTest or UnitTest
        case "TopicUnitTest":
        case "UnitTest":
            return "UnitTest";
        case "AIGuideActivity":
            return "AIGuideActivity";
        default:
            throw new KAError("Unknown content kind " + kind, Errors.Internal);
    }
};

export const ContentKindFromCode: Partial<
    Record<LearnableContentCode, LearnableContentKind>
> = {
    a: "Article",
    v: "Video",
    e: "Exercise",
    pc: "Challenge",
    pi: "Interactive",
    pp: "Project",
    pt: "Talkthrough",
    quiz: "Quiz",
    test: "UnitTest",
};

export const ContentCodeFromKind: Partial<
    Record<LearnableContentKind, LearnableContentCode>
> = {
    Article: "a",
    Video: "v",
    Exercise: "e",
    Challenge: "pc",
    Interactive: "pi",
    Project: "pp",
    Talkthrough: "pt",
    Quiz: "quiz",
    UnitTest: "test",
};

export function getPercentage(
    numAttempted?: number | null,
    numCorrect?: number | null,
): number {
    return Math.round(((numCorrect ?? 0) / (numAttempted ?? 1)) * 100);
}

// sync-start:get-progress-key 787276027 https://github.com/Khan/webapp/blob/master/services/progress/shared/progress_keys.go
/**
 * Helper function that returns the progress key for a given content kind and
 * id.
 */
export const getProgressKey = (
    kind: LearnableContentKind,
    id: string,
): string => {
    const contentCode = ContentCodeFromKind[kind];
    // This should never happen, but we will guard against it for TypeScript
    // and fail loudly to catch this in dev.
    if (!contentCode) {
        throw new KAError(
            "Invalid content kind for progress key",
            Errors.Internal,
        );
    }
    switch (kind) {
        case "Article":
        case "Exercise":
        case "Video":
        case "Interactive":
        case "Challenge":
        case "Project":
        case "Talkthrough":
            return contentCode + id;
        default:
            throw new KAError(
                "Invalid content kind for progress key",
                Errors.Internal,
            );
    }
};
// sync-end:get-progress-key

/**
 * A helper function that eturns the displayed content title.
 */
export const getDisplayedContentTitle = (
    metadata:
        | LessonChild
        | QuizMetadata
        | UnitTestMetadata
        | CourseChallenge
        | MasteryChallenge,
): string => {
    switch (metadata.contentKind) {
        case "Quiz":
            // Quiz indices begin at 0, but we'd like to display the 1-based
            // indexing to users so we increment the quiz index by 1.
            return strings.translatedQuizTitle(metadata.index + 1);

        case "UnitTest":
            return strings.translatedContentKind(metadata.contentKind);

        case "CourseChallenge":
            return t`Course challenge`;

        case "MasteryChallenge":
            return t`Mastery challenge`;

        default:
            return metadata.translatedTitle;
    }
};

export const ValidLearnableContentKinds: LearnableContentKind[] = [
    "Article",
    "Challenge",
    "Exercise",
    "Interactive",
    "Project",
    "Quiz",
    "Talkthrough",
    "UnitTest",
    "Video",
    "AIGuideActivity",
];
